import React, { useEffect, useState } from "react";
import { collection, query, where, orderBy, onSnapshot, limit, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import useIsMobile from "../../hooks/useIsMobile";
import AddIcon from "@mui/icons-material/Add";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";

const ThreadSidebar = ({ userId, threadId, onSelectThread, open, onClose, agentId }) => {
  const [threads, setThreads] = useState([]);
  const [lastMessages, setLastMessages] = useState({});
  const [loading, setLoading] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);

    const constraints = [
      where("userId", "==", userId),
    ];
    if (agentId) constraints.push(where("agentId", "==", agentId));
    constraints.push(orderBy("updatedAt", "desc"));

    const q = query(collection(db, "chatThreads"), ...constraints);

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        const items = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setThreads(items);
        setLoading(false);

        // Fetch last message for each thread
        const msgs = {};
        await Promise.all(
          items.map(async (t) => {
            try {
              const msgQ = query(
                collection(db, "chatThreads", t.id, "messages"),
                orderBy("createdAt", "desc"),
                limit(1)
              );
              const snap = await getDocs(msgQ);
              if (!snap.empty) {
                msgs[t.id] = snap.docs[0].data();
              }
            } catch (e) {
              // ignore per-thread errors
            }
          })
        );
        setLastMessages(msgs);
      },
      (err) => {
        console.error("Failed to fetch threads:", err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [open, userId, agentId]);

  if (!open) return null;

  const panel = (
    <div
      style={{
        width: "260px",
        borderRight: isMobile ? "none" : "1px solid #e8e8e8",
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        flexShrink: 0,
        ...(isMobile ? { position: "relative", zIndex: 1001 } : {}),
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "16px",
          borderBottom: "1px solid #e8e8e8",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ fontWeight: 600, fontSize: "14px", color: "#333" }}>
          Threads
        </span>
        <button
          onClick={() => onSelectThread(null)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            padding: "6px 10px",
            borderRadius: "6px",
            border: "1px solid #e0e0e0",
            backgroundColor: "#fff",
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: 500,
            color: "#825ee7",
          }}
          title="New Chat"
        >
          <AddIcon style={{ fontSize: "16px" }} />
          New
        </button>
      </div>

      {/* Thread list */}
      <div style={{ flex: 1, overflowY: "auto", overscrollBehavior: "contain", WebkitOverflowScrolling: "touch", padding: "8px" }}>
        {loading && threads.length === 0 && (
          <div style={{ color: "#999", fontSize: "13px", padding: "12px", textAlign: "center" }}>
            Loading...
          </div>
        )}
        {!loading && threads.length === 0 && (
          <div style={{ color: "#999", fontSize: "13px", padding: "12px", textAlign: "center" }}>
            No conversations yet
          </div>
        )}
        {threads.map((t) => {
          const isActive = t.id === threadId;
          const dateObj = t.updatedAt?.toDate?.() || t.createdAt?.toDate?.();
          const dateStr = dateObj
            ? dateObj.toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "";
          const lastMsg = lastMessages[t.id];
          const preview = lastMsg?.content
            ? `${lastMsg.role === "assistant" ? "AI: " : ""}${lastMsg.content.slice(0, 50)}${lastMsg.content.length > 50 ? "..." : ""}`
            : null;
          return (
            <button
              key={t.id}
              onClick={() => onSelectThread(t.id)}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
                width: "100%",
                padding: "10px 10px",
                borderRadius: "8px",
                border: "none",
                cursor: "pointer",
                fontSize: "13px",
                color: isActive ? "#825ee7" : "#555",
                backgroundColor: isActive ? "rgba(130,94,231,0.08)" : "transparent",
                fontWeight: isActive ? 600 : 400,
                textAlign: "left",
                marginBottom: "2px",
              }}
              onMouseEnter={(e) => {
                if (!isActive) e.currentTarget.style.backgroundColor = "#f5f5f7";
              }}
              onMouseLeave={(e) => {
                if (!isActive) e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <ChatBubbleOutlineIcon style={{ fontSize: "16px", flexShrink: 0, opacity: 0.5, marginTop: "2px" }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t.title || "New chat"}
                </div>
                {preview && (
                  <div style={{ fontSize: "11px", color: "#999", marginTop: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {preview}
                  </div>
                )}
                {dateStr && (
                  <div style={{ fontSize: "10px", color: "#bbb", marginTop: "2px" }}>
                    {dateStr}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  // Desktop: render inline
  if (!isMobile) return panel;

  // Mobile: fixed overlay with backdrop
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        display: "flex",
      }}
    >
      {panel}
      <div
        onClick={onClose}
        style={{
          flex: 1,
          backgroundColor: "rgba(0,0,0,0.4)",
        }}
      />
    </div>
  );
};

export default ThreadSidebar;
